import React, { useState } from "react";
import { styled } from "@mui/material/styles";
import { Tabs as MuiTabs, Tab, Stack, Button, Divider, Typography, Select, MenuItem, InputBase } from "@mui/material";
import IconButton from "@mui/material/IconButton";
import SearchIcon from "@mui/icons-material/Search";
import { globalTheme } from "../../styles";
import { ACTIVE_ORDERS_TABLE_TAB_KEY, ORDER_SHIPMENTS_SEARCH_TYPES, ORDERS_TABLE_TABS } from "../../constants";
import { AdvancedSearchIcon } from "../Icon";

interface OrdersTabsProps {
  onAdvancedSettingsOpen: () => void;
  onOrdersCustomSearch: (customSearchText: string, searchType: string) => void;
  onOrdersTableTabChange: (tabValue: string) => void;
}

export const OrdersTableTabs = ({ onAdvancedSettingsOpen, onOrdersCustomSearch, onOrdersTableTabChange }: OrdersTabsProps) => {
  //Used this condition to sync the submenu highlight which reflects in primary nav
  const storedTabValue = localStorage.getItem(ACTIVE_ORDERS_TABLE_TAB_KEY);
  if (!storedTabValue) {
    localStorage.setItem(ACTIVE_ORDERS_TABLE_TAB_KEY, ORDERS_TABLE_TABS.UN_PICKED.value);
  }
  const [activeOrdersTableTab, setActiveOrdersTableTab] = useState(
    localStorage.getItem(ACTIVE_ORDERS_TABLE_TAB_KEY) || ORDERS_TABLE_TABS.UN_PICKED.value
  );

  const [ordersCustomSearchText, setOrdersCustomSearchText] = useState<string>("");
  const [searchType, setSearchType] = useState("PRODUCT");

  const handleOrdersTableTabUpdate = (_event: React.SyntheticEvent, newTabValue: string) => {
    localStorage.setItem(ACTIVE_ORDERS_TABLE_TAB_KEY, newTabValue);
    setActiveOrdersTableTab(newTabValue);
    onOrdersTableTabChange(newTabValue);
  };

  const handleSearchTypeChange = (event) => {
    const newSearchType = event.target.value;
    setSearchType(newSearchType);
    onOrdersCustomSearch(ordersCustomSearchText, newSearchType);
  };

  return (
    <Stack direction="row" justifyContent="space-between" alignItems="center">
      <AntTabs
        value={activeOrdersTableTab}
        onChange={handleOrdersTableTabUpdate}
        aria-label="Orders table tabs"
        variant="scrollable"
        scrollButtons="auto"
      >
        <AntTab label={ORDERS_TABLE_TABS.UN_PICKED.label} value={ORDERS_TABLE_TABS.UN_PICKED.value} />
        <AntTab label={ORDERS_TABLE_TABS.ASSIGNED.label} value={ORDERS_TABLE_TABS.ASSIGNED.value} />
        <AntTab label={ORDERS_TABLE_TABS.ERROR.label} value={ORDERS_TABLE_TABS.ERROR.value} />
        <AntTab label={ORDERS_TABLE_TABS.ISSUES.label} value={ORDERS_TABLE_TABS.ISSUES.value} />
        <AntTab label={ORDERS_TABLE_TABS.ON_HOLD.label} value={ORDERS_TABLE_TABS.ON_HOLD.value} />
        <AntTab label={ORDERS_TABLE_TABS.CANCELLED.label} value={ORDERS_TABLE_TABS.CANCELLED.value} />
      </AntTabs>

      {/* TODO: This can be externalised to common module along with shipment page section  */}
      <Stack
        direction="row"
        justifyContent="space-around"
        alignItems="center"
        spacing={0.5}
        component="form"
        onSubmit={(e) => {
          e.preventDefault();
          onOrdersCustomSearch(ordersCustomSearchText, searchType);
        }}
        sx={{
          width: 500,
        }}
      >
        <IconButton type="submit" aria-label="search" size={"small"}>
          <SearchIcon sx={{ width: "16px", height: "16px" }} />
        </IconButton>

        <InputBase
          sx={{ ml: 1, flex: 1, fontSize: "14px" }}
          placeholder="Search order shipments"
          inputProps={{ "aria-label": "search order shipments" }}
          size="small"
          value={ordersCustomSearchText}
          onChange={(e) => setOrdersCustomSearchText(e.target.value)}
        />

        <Select
          labelId="sort-by-field-label-id"
          id="sort-by-field-id"
          value={searchType}
          onChange={handleSearchTypeChange}
          sx={{
            ".MuiSelect-select": {
              padding: 1,
              width: "100px",
            },
            fontSize: "0.75rem",
          }}
        >
          {Object.entries(ORDER_SHIPMENTS_SEARCH_TYPES).map(([key, value]) => (
            <MenuItem value={key} key={`${key}-menu-item`}>
              <div style={{ overflow: "hidden", textOverflow: "ellipsis" }}>{value}</div>
            </MenuItem>
          ))}
        </Select>

        <Divider orientation="vertical" variant="middle" flexItem />

        <Button onClick={onAdvancedSettingsOpen} style={{ textTransform: "none" }}>
          <Stack direction="row" justifyContent="space-around" alignItems="center" spacing={0.5}>
            <AdvancedSearchIcon fill={globalTheme.blue} width="16" height="16" />
            <Typography variant="caption" color={globalTheme.blue}>
              Advanced Settings
            </Typography>
          </Stack>
        </Button>
      </Stack>
    </Stack>
  );
};

const AntTabs = styled(MuiTabs)({
  borderBottom: `1px solid ${globalTheme.border}`,
  "& .MuiTabs-indicator": {
    backgroundColor: globalTheme.blue,
  },
});

interface StyledTabProps {
  label: string;
  value: string;
}

const AntTab = styled((props: StyledTabProps) => <Tab disableRipple {...props} />)(({ theme }) => ({
  textTransform: "none",
  minWidth: 0,
  fontWeight: theme.typography.fontWeightRegular,
  marginRight: theme.spacing(1),
  "&:hover": {
    color: globalTheme.blue,
    opacity: 1,
  },
  "&.Mui-selected": {
    color: globalTheme.blue,
    fontWeight: theme.typography.fontWeightMedium,
  },
}));
